/**
 * Brace linting for generated email templates.
 *
 * Templates carry two placeholder layers and both can fail silently at SEND
 * time, which is the worst possible moment to find out:
 *
 *   {single braces}   AI instructions, resolved per lead by WorldEmailCreator.
 *                     Its extractInstructionBlocks() regex cannot match nested
 *                     braces, and assembleResolvedTemplate() DELETES anything
 *                     it failed to resolve — leaving a sentence fragment.
 *
 *   {{double braces}} Static variables. The send-time body map is much smaller
 *                     than the editor's variable picker suggests, and every
 *                     unknown one is stripped to an empty string.
 *
 * So this runs right after generation and surfaces the problems while the user
 * can still fix them.
 */

/** Mirrors extractInstructionBlocks() in functions/WorldEmailCreator.js. */
const INSTRUCTION_REGEX = /(?<!\{)\{(?!\{)((?:[^{}]|\{\{[^{}]*\}\})+)\}(?!\})/g;

/**
 * Every {single brace AI instruction} in the text, braces included.
 *
 * Exported so anything comparing two templates reads the same blocks the send
 * pipeline will, instead of hand-rolling a second regex that drifts from it.
 *
 * @param {string} text template body
 * @returns {string[]} instruction blocks, e.g. ['{Nenne ein Produkt.}']
 */
export function extractInstructionBlocks(text) {
  if (!text) return [];
  INSTRUCTION_REGEX.lastIndex = 0;
  return text.match(INSTRUCTION_REGEX) || [];
}

/**
 * Variables the send-time BODY pipeline can actually resolve.
 *
 * Source of truth: the `varMap` in functions/WorldEmailCreator.js (Hyper fast
 * path) plus the set the Writer prompt advertises. Deliberately NOT the
 * editor's dynamicKeywords list — that one is written for subject lines, where
 * NormalMode's replacePlaceholders() resolves a much larger catalogue.
 */
export const SEND_TIME_BODY_VARIABLES = [
  'business_name',
  'name',
  'first_name',
  'company',
  'website',
  'recipient_name',
  'recipient_role',
  'sender_name',
  'sender_email',
  'greeting',
  'footer_text',
];

/**
 * Picker variables that look valid but evaluate to an empty string in the body.
 * Each maps to the equivalent that does work, so we can offer a real fix.
 */
export const BODY_VARIABLE_ALIASES = {
  lead_firstname: 'first_name',
  lead_name: 'name',
  lead_lastname: 'name',
  lead_website: 'website',
  company_name: 'sender_name',
  user_name: 'sender_name',
  user_email: 'sender_email',
  lead_business: 'business_name',
};

/** Every {{token}} in the text, including malformed ones. */
function collectDoubleBraces(text) {
  const found = [];
  const regex = /\{\{\s*([^{}]*?)\s*\}\}/g;
  let match = regex.exec(text);
  while (match) {
    found.push(match[1]);
    match = regex.exec(text);
  }
  return found;
}

/**
 * Detect `{ ... { ... } ... }`. The send-time regex silently fails on these,
 * so the whole block is dropped instead of resolved.
 */
function findNestedInstructions(text) {
  const hits = [];
  let depth = 0;
  let start = -1;
  let sawInner = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const isDoubleOpen = ch === '{' && text[i + 1] === '{';
    const isDoubleClose = ch === '}' && text[i + 1] === '}';
    if (isDoubleOpen || isDoubleClose) {
      i += 1;
      continue;
    }
    if (ch === '{') {
      depth += 1;
      if (depth === 1) {
        start = i;
        sawInner = false;
      } else {
        sawInner = true;
      }
    } else if (ch === '}') {
      if (depth === 1 && sawInner && start !== -1) {
        hits.push(text.slice(start, i + 1));
      }
      depth = Math.max(depth - 1, 0);
      if (depth === 0) start = -1;
    }
  }
  return hits;
}

/** Are single braces balanced overall? */
function bracesBalanced(text) {
  const stripped = text.replace(/\{\{[^{}]*\}\}/g, '');
  let depth = 0;
  for (const ch of stripped) {
    if (ch === '{') depth += 1;
    else if (ch === '}') depth -= 1;
    if (depth < 0) return false;
  }
  return depth === 0;
}

/**
 * Lint one generated template.
 *
 * @param {string} text template body
 * @param {Object} [options]
 * @param {string[]} [options.customFieldKeys] user custom fields — these ARE
 *   resolvable at send time and must not be reported as unknown
 * @param {boolean} [options.smartGreeting] whether the template resolves
 *   {{greeting}} through the greeting micro-call
 * @returns {{instructionCount: number, issues: Array<{level: string, code: string,
 *   detail: string, fix?: string}>}}
 */
export function checkTemplateBraces(text, options = {}) {
  const { customFieldKeys = [], smartGreeting = false } = options;
  const issues = [];

  if (!text || !text.trim()) {
    return { instructionCount: 0, issues };
  }

  const instructions = extractInstructionBlocks(text);

  if (!bracesBalanced(text)) {
    issues.push({
      level: 'error',
      code: 'unbalanced',
      detail: 'Unbalanced single braces — an instruction is missing its opening or closing brace.',
    });
  }

  findNestedInstructions(text).forEach((block) => {
    issues.push({
      level: 'error',
      code: 'nested',
      detail: `Nested braces: ${block.slice(0, 80)}${block.length > 80 ? '…' : ''}`,
      fix: 'The send-time parser cannot read nested braces and drops the whole block. Flatten it into one instruction.',
    });
  });

  const known = new Set([...SEND_TIME_BODY_VARIABLES, ...customFieldKeys]);
  const seen = new Set();

  collectDoubleBraces(text).forEach((raw) => {
    const key = raw.trim();
    if (!key || key.startsWith('image:') || seen.has(key)) return;
    seen.add(key);

    if (key === 'greeting' && !smartGreeting) {
      issues.push({
        level: 'warning',
        code: 'greeting-off',
        detail: '{{greeting}} is used but Smart Greeting is off for this template.',
        fix: 'Turn on Smart Greeting, or write the salutation as plain text.',
      });
      return;
    }
    if (known.has(key)) return;

    const alias = BODY_VARIABLE_ALIASES[key];
    issues.push({
      level: 'error',
      code: 'unknown-variable',
      detail: `{{${key}}} is not resolved in the email body.`,
      fix: alias
        ? `Use {{${alias}}} instead — {{${key}}} is stripped to an empty string at send time.`
        : 'Only body variables and your own custom fields resolve here; everything else is stripped to an empty string.',
    });
  });

  // A trailing line made only of variables is the model reflexively signing the
  // email. The system appends the real signature, so at send time this renders
  // as a bare company name dangling under the CTA.
  const lastLine = text.split('\n').map(l => l.trim()).filter(Boolean).pop() || '';
  const withoutFooterSnippet = lastLine.replace(/\{\{\s*footer_text\s*\}\}/gi, 'x');
  const withoutVars = withoutFooterSnippet.replace(/\{\{[^{}]*\}\}/g, '').replace(/[\s,.\-–—|]/g, '');
  if (!withoutVars && /\{\{[^{}]*\}\}/.test(lastLine)) {
    issues.push({
      level: 'error',
      code: 'dangling-tail',
      detail: `The template ends on a line containing nothing but variables: "${lastLine}".`,
      fix: 'Delete it. The signature is appended automatically, so this leaves a stray name under the CTA.',
    });
  }

  if (instructions.length === 0) {
    issues.push({
      level: 'warning',
      code: 'no-instructions',
      detail: 'No {AI instructions} in this template — it will read identically for every lead.',
      fix: 'Leave framework fields empty to have them generated per lead, or add instructions by hand.',
    });
  }

  return { instructionCount: instructions.length, issues };
}

/**
 * Is the polished template safe to keep instead of the draft?
 *
 * The refinement pass is told to strip braces off static text, and it sometimes
 * over-applies that rule and de-brackets the ENTIRE template — turning every AI
 * instruction into literal email prose ("Finde in den Web Research Daten ein
 * aktuelles Ereignis…" shipped to the prospect). Observed in an end-to-end run:
 * a clean 4-instruction draft came back with zero.
 *
 * Removing one or two instructions is the pass doing its job. Removing most of
 * them is the failure mode, so that is where the line sits.
 *
 * @param {string} draft template before the polish pass
 * @param {string} refined template after it
 * @returns {{safe: boolean, reason?: string, draftCount: number, refinedCount: number}}
 */
export function refinementIsSafe(draft, refined) {
  const draftCount = checkTemplateBraces(draft).instructionCount;
  const refinedCount = checkTemplateBraces(refined || '').instructionCount;

  if (!refined || !refined.trim()) {
    return { safe: false, reason: 'empty', draftCount, refinedCount };
  }
  if (draftCount > 0 && refinedCount === 0) {
    return { safe: false, reason: 'all_instructions_removed', draftCount, refinedCount };
  }
  if (refinedCount * 2 < draftCount) {
    return { safe: false, reason: 'most_instructions_removed', draftCount, refinedCount };
  }
  return { safe: true, draftCount, refinedCount };
}

export default checkTemplateBraces;
